import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Grid,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Stack,
  ListItemButton,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { downloadCSV } from "../lib/exportCsv.ts";
import { config, CsvEncoding } from "../lib/config.ts";
import { CsvEncodingSelect } from "./parts/CsvEncodingSelect.tsx";
import { transformFromMasterToCSV } from "../lib/transform/transformFromMasterToCsv.ts";
import { ICategory } from "../models/category.ts";
import { ISection } from "../models/section.ts";
import { transformFromSectionToCSV } from "../lib/transform/transformFromSectionToCsv.ts";
import { IPermissionGroup } from "../models/permission_group.ts";
import { IContentTag } from "../models/content_tag.ts";
import { getCurrentDateTime } from "../lib/getCurrentDateTime.ts";
import { proxyHttpRequest } from "../lib/api/proxyHttpRequest.ts";
import { exportArticlesAndAttachments } from "../usecases/exportArticlesAndAttachments.ts";
import {
  ExportProgressModal,
  ExportProgressStatus,
} from "./parts/ExportProgressModal.tsx";
import { getPaginatedData } from "../lib/api/getPaginatedData.ts";
import { IUserSegment } from "../models/user_segment.ts";

export const Export: React.FC<{ token: string; selectBrandUrl: string }> = ({
  token,
  selectBrandUrl,
}) => {
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const [totalArticlesCount, setTotalArticlesCount] = useState<number>(0);
  const [downloadedArticlesCount, setDownloadedArticlesCount] =
    useState<number>(0);
  const [exportProgressInfo, setExportProgressInfo] = useState<{
    status: ExportProgressStatus;
    message: string;
  }>({ status: "Export", message: "" });

  const [createdStartDate, setCreatedStartDate] = useState<string>("");
  const [createdEndDate, setCreatedEndDate] = useState<string>("");
  const [updatedStartDate, setUpdatedStartDate] = useState<string>("");
  const [updatedEndDate, setUpdatedEndDate] = useState<string>("");
  const [titleKeyword, setTitleKeyword] = useState<string>("");
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<ICategory[]>([]);
  const [sections, setSections] = useState<ISection[]>([]);
  const [selectedSections, setSelectedSections] = useState<ISection[]>([]);
  const [csvEncoding, setCsvEncoding] = useState<CsvEncoding>(
    config.defaultCsvEncoding,
  );

  // ブランド切り替え時のみカテゴリ/セクション選択をリセットする
  // （token はバックグラウンドで自動更新されるため依存に含めない）
  useEffect(() => {
    setSelectedCategories([]);
    setSelectedSections([]);
  }, [selectBrandUrl]);

  useEffect(() => {
    if (!selectBrandUrl) {
      setCategories([]);
      setSections([]);
      return;
    }
    const fetchMasters = async () => {
      try {
        const [categoryData, sectionData] = await Promise.all([
          getPaginatedData<ICategory>(
            "help_center/categories.json",
            token,
            selectBrandUrl,
            "categories",
          ),
          getPaginatedData<ISection>(
            "help_center/sections.json",
            token,
            selectBrandUrl,
            "sections",
          ),
        ]);
        setCategories(categoryData);
        setSections(sectionData);
      } catch (error) {
        console.error("カテゴリー/セクションの取得に失敗しました:", error);
      }
    };
    fetchMasters();
  }, [token, selectBrandUrl]);

  // セクション選択可否: カテゴリを 1 件だけ選択しているときのみ
  const isSectionSelectable = selectedCategories.length === 1;
  const sectionOptions = isSectionSelectable
    ? sections.filter((s) => s.category_id === selectedCategories[0].id)
    : [];
  const sectionHelperText = (() => {
    if (selectedCategories.length === 0) {
      return "カテゴリを 1 件選択するとセクションを選べます";
    }
    if (selectedCategories.length > 1) {
      return "カテゴリを複数選択中のためセクションは選べません";
    }
    return "";
  })();

  const handleDateChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (newValue: Dayjs | null) => {
      setter(newValue ? newValue.format("YYYY-MM-DD") : "");
    };

  const handleUpdateTotalArticlesCount = async (count: number) => {
    setTotalArticlesCount(count);
  };
  const handleUpdateDownloadedArticlesCount = async (count: number) => {
    setDownloadedArticlesCount(count);
  };
  const handleUpdateProgressInfo = async (
    status: ExportProgressStatus,
    message: string,
  ) => {
    setExportProgressInfo({ status: status, message: message });
  };

  const handleDownload = async (
    fetchFunction: (brandUrl: string, token: string) => Promise<any[]>,
    transformFunction: (data: any) => any,
    fileName: string,
    errorMessage: string,
  ) => {
    try {
      const data = await fetchFunction(selectBrandUrl, token);
      downloadCSV(
        data.map(transformFunction),
        fileName + "-" + getCurrentDateTime(),
        csvEncoding,
      );
    } catch (error) {
      console.error(`${errorMessage}: ${error}`);
    }
  };
  const boxStyle = {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    p: 3,
  };

  return (
    <Grid item>
      <Stack spacing={3}>
        <Box sx={boxStyle}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
            記事エクスポート
          </Typography>
          {/* 作成日・更新日（横並び） */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" textAlign="left" sx={{ mb: 1 }}>
                作成日（期間指定）
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="開始日"
                      format="YYYY/MM/DD"
                      slotProps={{ calendarHeader: { format: "YYYY年MM月" } }}
                      value={createdStartDate ? dayjs(createdStartDate) : null}
                      onChange={handleDateChange(setCreatedStartDate)}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="終了日"
                      format="YYYY/MM/DD"
                      slotProps={{ calendarHeader: { format: "YYYY年MM月" } }}
                      value={createdEndDate ? dayjs(createdEndDate) : null}
                      onChange={handleDateChange(setCreatedEndDate)}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2" textAlign="left" sx={{ mb: 1 }}>
                更新日（期間指定）
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="開始日"
                      format="YYYY/MM/DD"
                      slotProps={{ calendarHeader: { format: "YYYY年MM月" } }}
                      value={updatedStartDate ? dayjs(updatedStartDate) : null}
                      onChange={handleDateChange(setUpdatedStartDate)}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="終了日"
                      format="YYYY/MM/DD"
                      slotProps={{ calendarHeader: { format: "YYYY年MM月" } }}
                      value={updatedEndDate ? dayjs(updatedEndDate) : null}
                      onChange={handleDateChange(setUpdatedEndDate)}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <Autocomplete
                multiple
                options={categories}
                getOptionLabel={(option) => option.name}
                value={selectedCategories}
                onChange={(_e, newValue) => {
                  setSelectedCategories(newValue);
                  // カテゴリ変更時は整合しないセクションを落とす
                  if (newValue.length === 1) {
                    const catId = newValue[0].id;
                    setSelectedSections((prev) =>
                      prev.filter((s) => s.category_id === catId),
                    );
                  } else {
                    setSelectedSections([]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="カテゴリー"
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="記事タイトルキーワード"
                variant="outlined"
                value={titleKeyword}
                onChange={(e) => setTitleKeyword(e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                multiple
                disabled={!isSectionSelectable}
                options={sectionOptions}
                getOptionLabel={(option) => option.name}
                value={selectedSections}
                onChange={(_e, newValue) => setSelectedSections(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="セクション"
                    variant="outlined"
                    placeholder={
                      isSectionSelectable ? "" : "カテゴリを 1 件選択してください"
                    }
                    helperText={sectionHelperText}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <CsvEncodingSelect value={csvEncoding} onChange={setCsvEncoding} />
            </Grid>
          </Grid>

          <Button
            variant="contained"
            sx={{
              mt: 2,
              backgroundColor: "#00897B",
              "&:hover": { backgroundColor: "#00695C" },
            }}
            onClick={async () => {
              setVisibleModal(true);
              await exportArticlesAndAttachments({
                brandUrl: selectBrandUrl,
                token,
                createdStartDate,
                createdEndDate,
                updatedStartDate,
                updatedEndDate,
                titleKeyword,
                categoryIds: selectedCategories.map((c) => c.id),
                sectionIds: selectedSections.map((s) => s.id),
                onUpdateTotalCount: handleUpdateTotalArticlesCount,
                onUpdateDownloadedCount: handleUpdateDownloadedArticlesCount,
                onUpdateInfo: handleUpdateProgressInfo,
                csvEncoding,
              });
            }}
          >
            エクスポート実行
          </Button>
        </Box>

        <Box sx={boxStyle}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
            マスターデータダウンロード
          </Typography>
          <List>
            <ListItem>
              <ListItemButton
                onClick={() =>
                  handleDownload(
                    async (brandUrl: string, token: string) => {
                      return await getPaginatedData<ICategory>(
                        "help_center/categories.json",
                        token,
                        brandUrl,
                        "categories",
                      );
                    },
                    transformFromMasterToCSV,
                    "categories",
                    "カテゴリーの取得に失敗しました",
                  )
                }
              >
                <ListItemText primary="カテゴリ" />
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                onClick={async () => {
                  try {
                    const categories = await getPaginatedData<ICategory>(
                      "help_center/categories.json",
                      token,
                      selectBrandUrl,
                      "categories",
                    );
                    const sections = await getPaginatedData<ISection>(
                      "help_center/sections.json",
                      token,
                      selectBrandUrl,
                      "sections",
                    );
                    downloadCSV(
                      sections.map((section) =>
                        transformFromSectionToCSV(section, categories),
                      ),
                      "sections-" + getCurrentDateTime(),
                      csvEncoding,
                    );
                  } catch (error) {
                    console.error(error + "セクションの取得に失敗しました。");
                  }
                }}
              >
                <ListItemText primary="セクション" />
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                onClick={() =>
                  handleDownload(
                    async (brandUrl: string, token: string) => {
                      const data = await proxyHttpRequest<{
                        permission_groups: IPermissionGroup[];
                      }>(token, brandUrl + "/api/v2/guide/permission_groups");
                      return data.permission_groups;
                    },
                    transformFromMasterToCSV,
                    "permission groups",
                    "管理者の取得に失敗しました",
                  )
                }
              >
                <ListItemText primary="管理者" />
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                onClick={() =>
                  handleDownload(
                    async (selectBrandUrl: string, token: string) => {
                      return await getPaginatedData<IUserSegment>(
                        "help_center/user_segments",
                        token,
                        selectBrandUrl,
                        "user_segments",
                      );
                    },
                    transformFromMasterToCSV,
                    "user segments",
                    "ユーザーセグメントの取得に失敗しました",
                  )
                }
              >
                <ListItemText primary="ユーザーセグメント" />
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                onClick={() =>
                  handleDownload(
                    async (brandUrl: string, token: string) => {
                      const data = await proxyHttpRequest<{
                        records: IContentTag[];
                      }>(token, brandUrl + "/api/v2/guide/content_tags");
                      return data.records;
                    },
                    transformFromMasterToCSV,
                    "content tags",
                    "コンテンツタグの取得に失敗しました",
                  )
                }
              >
                <ListItemText primary="コンテンツタグ" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Stack>

      <ExportProgressModal
        open={visibleModal}
        onClose={() => {
          setVisibleModal(false);
          setTotalArticlesCount(0);
          setDownloadedArticlesCount(0);
          setExportProgressInfo({ status: "Export", message: "" });
        }}
        totalCount={totalArticlesCount}
        downloadedCount={downloadedArticlesCount}
        progressInfo={exportProgressInfo}
      />
    </Grid>
  );
};
